"use client";

import { useState } from "react";
import { NOTE_NAMES, type KeyCandidate } from "../lib/music";
import {
  createManualKey,
  keyCandidateLabel,
  type HarmonyGuideMode,
  type LiveKeyAnalysis,
  type LiveKeyEvidence,
  type LiveKeySource,
} from "../lib/live-key-guide";

type Props = {
  open: boolean;
  finderOpen: boolean;
  selectedKey: KeyCandidate | null;
  keySource: LiveKeySource;
  guideEnabled: boolean;
  harmonyMode: HarmonyGuideMode;
  captureCandidates: KeyCandidate[];
  evidence: LiveKeyEvidence[];
  analysis: LiveKeyAnalysis;
  onClose: () => void;
  onOpenFinder: () => void;
  onDoneFinder: () => void;
  onUndo: () => void;
  onClear: () => void;
  onUseKey: (candidate: KeyCandidate, source: Exclude<LiveKeySource, "none">) => void;
  onOff: () => void;
  onGuideChange: (enabled: boolean) => void;
  onHarmonyChange: (mode: HarmonyGuideMode) => void;
};

const SOURCE_LABELS: Record<LiveKeySource, string> = {
  none: "OFF",
  capture: "CAPTURE RESULT",
  "live-estimate": "LIVE ESTIMATE",
  manual: "MANUAL",
};

const HARMONY_OPTIONS: Array<{ value: HarmonyGuideMode; label: string }> = [
  { value: "off", label: "OFF" },
  { value: "third-below", label: "3RD BELOW" },
  { value: "third-above", label: "3RD ABOVE" },
  { value: "sixth-below", label: "6TH BELOW" },
  { value: "sixth-above", label: "6TH ABOVE" },
];

function CandidateList({ candidates, source, onUse }: {
  candidates: KeyCandidate[];
  source: "capture" | "live-estimate";
  onUse: (candidate: KeyCandidate, source: "capture" | "live-estimate") => void;
}) {
  if (!candidates.length) return <p className="live-key-empty">候補はまだありません</p>;
  return <div className="live-key-candidates">{candidates.slice(0, 3).map((candidate, index) => (
    <button type="button" key={candidate.tonic + candidate.mode} onClick={() => onUse(candidate, source)}>
      <b>{index + 1}</b>
      <strong>{keyCandidateLabel(candidate)}</strong>
      <small>候補度 {candidate.confidence} · score {candidate.score.toFixed(2)}</small>
    </button>
  ))}</div>;
}

export function LiveKeyGuidePanel(props: Props) {
  const [manualTonic, setManualTonic] = useState(0);
  const [manualMode, setManualMode] = useState<"major" | "minor">("major");
  if (!props.open) return null;
  return <>
    {!props.finderOpen && <button type="button" className="live-key-backdrop" aria-label="Key設定を閉じる" onClick={props.onClose} />}
    <aside className="live-key-panel" role="dialog" aria-modal={!props.finderOpen} aria-label="LIVE PIANO KeyとHarmony設定">
      <header>
        <div><span>LIVE PIANO GUIDE</span><strong>KEY · SCALE · HARMONY</strong></div>
        <button type="button" onClick={props.onClose} aria-label="閉じる">×</button>
      </header>

      <div className="live-key-current">
        <span>{SOURCE_LABELS[props.keySource]}</span>
        <strong>{keyCandidateLabel(props.selectedKey)}</strong>
        <button type="button" onClick={props.onOff}>OFF</button>
      </div>

      {props.finderOpen ? (
        <section className="live-key-finder">
          <span>KEY FINDER / キーを探す</span>
          <p>メロディで使う予定の音を画面鍵盤またはMIDIピアノで押してください。主音らしい音があれば最初に押すと候補が安定します。</p>
          <div className="live-key-evidence">
            {props.analysis.uniquePitchClasses.map((noteClass) => <b key={noteClass}>{NOTE_NAMES[noteClass]}</b>)}
            {!props.evidence.length && <small>鍵盤入力待ち</small>}
          </div>
          <div className="live-key-strength">入力 {props.analysis.totalInputs} · Pitch Class {props.analysis.uniquePitchClasses.length} · 信頼度 {props.analysis.strength}</div>
          <CandidateList candidates={props.analysis.candidates} source="live-estimate" onUse={props.onUseKey} />
          <footer>
            <button type="button" onClick={props.onUndo} disabled={!props.evidence.length}>UNDO</button>
            <button type="button" onClick={props.onClear} disabled={!props.evidence.length}>CLEAR</button>
            <button type="button" className="primary" onClick={props.onDoneFinder}>DONE</button>
          </footer>
        </section>
      ) : (
        <>
          <section>
            <div className="live-key-section-head"><span>CAPTURE RESULT</span><small>録音候補をコピーして使用</small></div>
            <CandidateList candidates={props.captureCandidates} source="capture" onUse={props.onUseKey} />
          </section>

          <section className="live-key-actions">
            <button type="button" className="finder-launch" onClick={props.onOpenFinder}>⌕ キーを探す</button>
            <div>
              <select value={manualTonic} onChange={(event) => setManualTonic(Number(event.target.value))} aria-label="Manual tonic">
                {NOTE_NAMES.map((name, index) => <option key={name} value={index}>{name}</option>)}
              </select>
              <select value={manualMode} onChange={(event) => setManualMode(event.target.value as "major" | "minor")} aria-label="Manual scale">
                <option value="major">Major</option>
                <option value="minor">Minor</option>
              </select>
              <button type="button" onClick={() => props.onUseKey(createManualKey(manualTonic, manualMode), "manual")}>MANUALを使う</button>
            </div>
          </section>

          <section className="live-key-options">
            <label><input type="checkbox" checked={props.guideEnabled} disabled={!props.selectedKey} onChange={(event) => props.onGuideChange(event.target.checked)} /> KEY GUIDE</label>
            <label><span>HARMONY</span><select value={props.harmonyMode} disabled={!props.selectedKey || !props.guideEnabled} onChange={(event) => props.onHarmonyChange(event.target.value as HarmonyGuideMode)}>
              {HARMONY_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select></label>
            <small>紫はDiatonic 3rd / 6th候補です。自動発音しません。Chord候補は既存GUIDEで確認できます。</small>
          </section>
        </>
      )}
    </aside>
  </>;
}
